// Basic
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
// Store & actions
import useSessionStore from '@/app/store/session';
import useCustomPayStore from '@/app/store/customPay';
import actionsPosTerminals from '../../api/actionsPosTerminals';
import actionsPlans from '@/app/modules/plans/api/actionsPlans';
// Components
import PageActions from '@/common-utils/components/renders/pageActions/PageActions';
import ModalActivateSubscription from '@/app/modules/subscriptions/components/modals/ModalActivateSubscription';
import ModalTerminateSubscription from '@/app/modules/subscriptions/components/modals/ModalTerminateSubscription';
import ModalScheduledTerminateSubscription from '@/app/modules/subscriptions/components/modals/ModalScheduledTerminateSubscription';
import ModalMigrateSubscription from '@/app/modules/subscriptions/components/modals/ModalMigrateSubscription';
import ModalUpdateSubscriptionWithOrWithoutPlan from '@/app/modules/subscriptions/components/modals/ModalUpdateSubscriptionWithOrWithoutPlan';
import ModalAddEnvelope from '@/app/modules/subscriptions/components/modals/ModalAddEnvelope';
import ModalShowPosTerminalInfo from '../modals/status/ModalShowPosTerminalInfo';
import ModalUpdateLabels from '../modals/updateLabels/ModalUpdateLabels';
import ModalScheduledBlacklistPosTerminal from '../modals/status/ModalScheduledBlacklistPosTerminal';
import ModalRewarehousePosTerminal from '../modals/rewarehouse/ModalRewarehousePosTerminal';
import ModalUnblacklistPosTerminal from '../modals/status/ModalUnblacklistPosTerminal';
import ModalBlacklistPosTerminal from '../modals/status/ModalBlacklistPosTerminal';
import ModalAllowTemporarily from '../modals/status/ModalAllowTemporarily';
import ModalEmergencyRemoteCollection from '@/app/modules/payludd/subscriptions/components/modals/ModalEmergencyRemoteCollection';
import Entity from '@/common-utils/components/renders/entity/Entity';
import DetailsGrid from '@/common-utils/components/renders/detailsGrid/DetailsGrid';
import Icon from '@/common-utils/components/renders/icon/Icon';
import OpenStreetMapComponent from '@/common-utils/components/renders/openStreetMap/OpenStreetMapComponent';
import PayluddPosTerminalDetails from '@/app/modules/payludd/terminalState/components/PayluddPosTerminalDetails';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/common-utils/components/ui/accordion';
import { Skeleton } from '@/common-utils/components/ui/skeleton';
// Assets
import paceLogoSrc from 'url:../../../../../../resource/images/Logo_Payludd_N&B.png';
// Common
import subscriptionFields from '@/app/modules/subscriptions/fields/subscriptionFields';
import ICONS from '@/common-utils/enums/icons';
import Permissions from '@/app/permissions/Permissions';
import generateNestedFieldAccessor from '@/common-utils/functions/generateNestedFieldAccessor';
import getObjectAttributeFromStringKey from '@/common-utils/functions/getObjectAttributeFromStringKey';
import CustomPayGenerateActionsSubscriptionLifecycle from '@/app/modules/subscriptions/components/dropdown/actions/CustomPayGenerateActionsSubscriptionLifecycle';
import GenerateActionsSubscriptionLifecycle from '@/app/modules/subscriptions/components/dropdown/actions/generateActionsSubscriptionLifecycle';
import GenerateActionsPosTerminalLifecycle from '../dropdown/actions/generateActionsPosTerminalLifecycle';
import POS_TERMINAL_DETAILS_SECTIONS from '../../enums/PosTerminalDetailsSections';
import hasRequiredPermissions from '@/common-utils/functions/hasRequiredPermissions';
import POS_TERMINAL_STATUS from '../../enums/PosTerminalStatus';
import SUBSCRIPTION_STATUS from '@/app/modules/subscriptions/enums/SubscriptionStatus';
// Lang
import { nsPayluddSubscription, nsSubscription, nsTerminal } from '@/app/i18n/namespaces';

/**
 * @param root0
 * @param root0.subscriptionAndTerminal
 * @param root0.refetch
 * @returns {JSX} PosTerminalDetailsTab
 */
function PosTerminalDetailsTab({ subscriptionAndTerminal, refetch }) {

    // Store
    const permissions = useSessionStore(s => s.session.permissions);
    const isCustomPay = useCustomPayStore(s => s.isCustomPay);

    // States
    const [subscriptionDetailsGridData, setSubscriptionDetailsGridData] = useState(subscriptionAndTerminal);

    // Functions
    const hasPlanDetailsPermission = hasRequiredPermissions({
        permissions,
        requiredPermissions: [Permissions.SUBSCRIPTION_PLAN_READ.name]
    });
    // Helper function to filter out null and undefined values from an object
    const filterNullOrEmptyValues = (object) =>
        Object.fromEntries(
            Object
                .entries(object)
                .filter(([, field]) => {
                    const value = getObjectAttributeFromStringKey(
                        subscriptionAndTerminal,
                        generateNestedFieldAccessor(field)
                    );
                    return value !== null && value !== undefined && value.length;
                }
                ));

    // Constants
    // Fields to display
    const posTerminalDataToDisplay = {
        serialNumber: subscriptionFields.POS_TERMINAL.SERIAL_NUMBER,
        status: subscriptionFields.POS_TERMINAL.STATUS,
        label: subscriptionFields.POS_TERMINAL.LABEL,
        customLabel: subscriptionFields.POS_TERMINAL.CUSTOM_LABEL,
        manufacturer: subscriptionFields.POS_TERMINAL.MANUFACTURER,
        createdAt: subscriptionFields.POS_TERMINAL.CREATED_AT,
        updatedAt: subscriptionFields.POS_TERMINAL.UPDATED_AT,
        ...filterNullOrEmptyValues({
            blacklistStartDate: subscriptionFields.POS_TERMINAL.BLACKLIST_START_DATE,
            rewarehousedAt: subscriptionFields.POS_TERMINAL.REWAREHOUSE_DATE
        })
    };
    const subscriptionDataToDisplay = {
        status: subscriptionFields.STATUS,
        lastConnectionDate: subscriptionFields.LAST_CONNECTION_DATE,
        createdAt: subscriptionFields.CREATED_AT,
        activationDate: subscriptionFields.ACTIVATION_DATE,
        billingDate: subscriptionFields.START_BILLING_DATE,
        ...filterNullOrEmptyValues({
            resiliationDate: subscriptionFields.RESILIATION_DATE
        }),
        updatedAt: subscriptionFields.UPDATED_AT,
        legalName: subscriptionFields.CLIENT.LEGAL_NAME,
        clientCode: subscriptionFields.CLIENT.CLIENT_CODE,
        ...(hasPlanDetailsPermission && {
            planLabel: subscriptionFields.SUBSCRIPTION_PLAN.LABEL,
            planProductCode: subscriptionFields.SUBSCRIPTION_PLAN.PRODUCT_CODE
        })
    };
    const eventLogDataToDisplay = filterNullOrEmptyValues({
        lastSourceIp: subscriptionFields.LAST_SOURCE_IP,
        lastConnectionDateIp: subscriptionFields.POS_TERMINAL.LAST_CONNECTION_IP,
        lastConnectionDateGprs: subscriptionFields.POS_TERMINAL.LAST_CONNECTION_GPRS,
        lastConnectionType: subscriptionFields.LAST_CONNECTION_TYPE,
        simActivationDate: subscriptionFields.POS_TERMINAL.SIM.SIM_ACTIVATED_ON,
        simNumber: subscriptionFields.ACTIVE_ICCID,
        simStatus: subscriptionFields.POS_TERMINAL.SIM.SIM_STATUS,
        lastIccidOperator: subscriptionFields.LAST_ICCID_OPERATOR,
        simOffer: subscriptionFields.POS_TERMINAL.SIM.SIM_OFFER.SHORT_DISPLAY_NAME
    });
    // Props
    const isSubscriptionNotActivatedAndTerminalNotAllowed = subscriptionAndTerminal[subscriptionFields.STATUS.key] !== SUBSCRIPTION_STATUS.ACTIVATED.name
        && subscriptionAndTerminal[subscriptionFields.POS_TERMINAL.STATUS.key] !== POS_TERMINAL_STATUS.ALLOWED.name;
    const directProviderId = getObjectAttributeFromStringKey(
        subscriptionAndTerminal,
        generateNestedFieldAccessor(subscriptionFields.POS_TERMINAL.DIRECT_PROVIDER.PROVIDER_UUID)
    );
    const allowedClientId = subscriptionAndTerminal[subscriptionFields.MERCHANT_ORGANIZATION.key];
    // PAYLUDD -- Pos Terminal data to display
    const posTerminal = subscriptionAndTerminal?.posTerminal;
    const payludd = posTerminal?.payludd;
    const pace = posTerminal?.isBroadcast;
    const payluddHeaderLogoSrc = (payludd?.logo && String(payludd.logo).trim()) ? payludd.logo : undefined;
    const payluddHeaderColor = (payludd?.color && String(payludd.color).trim()) ? payludd.color : 'PACE';

    // States
    const [openSubscription, setOpenSubscription] = useState(true);
    const [openTerminal, setOpenTerminal] = useState(true);
    const [openEvents, setOpenEvents] = useState(true);
    const [openPayludd, setOpenPayludd] = useState(true);
    const [latitude, setLatitude] = useState(undefined);
    const [longitude, setLongitude] = useState(undefined);

    // Modals functions for subscriptions
    const [showModalActivateSubscription, setShowModalActivateSubscription] = useState(false);
    const handleShowModalActivateSubscription = () => {
        setShowModalActivateSubscription(true);
    };
    const [showModalTerminateSubscription, setShowModalTerminateSubscription] = useState(false);
    const handleShowModalTerminateSubscription = () => {
        setShowModalTerminateSubscription(true);
    };
    const [showModalScheduledTerminateSubscription, setShowModalScheduledTerminateSubscription] = useState(false);
    const handleShowModalScheduledTerminateSubscription = () => {
        setShowModalScheduledTerminateSubscription(true);
    };
    const [showModalMigrateSubscription, setShowModalMigrateSubscription] = useState(false);
    const handleShowModalMigrateSubscription = () => {
        setShowModalMigrateSubscription(true);
    };
    const [showModalUpdateSubscriptionWithOrWithoutPlan, setShowModalUpdateSubscriptionWithOrWithoutPlan] = useState(false);
    const handleShowModalUpdateSubscriptionWithOrWithoutPlan = () => {
        setShowModalUpdateSubscriptionWithOrWithoutPlan(true);
    };
    const [showModalAddEnvelope, setShowModalAddEnvelope] = useState(false);

    // Modals functions for POS terminals
    const [showModalTemporarilyAllow, setShowModalTemporarilyAllow] = useState(false);
    const handleShowModalTemporarilyAllow = () => {
        setShowModalTemporarilyAllow(true);
    };
    const [showModalBlacklistPosTerminal, setShowModalBlacklistPosTerminal] = useState(false);
    const handleShowModalBlacklistPosTerminal = () => {
        setShowModalBlacklistPosTerminal(true);
    };
    const [showModalUnblacklistPosTerminal, setShowModalUnblacklistPosTerminal] = useState(false);
    const handleShowModalUnblacklistPosTerminal = () => {
        setShowModalUnblacklistPosTerminal(true);
    };
    const [showModalRewarehousePosTerminal, setShowModalRewarehousePosTerminal] = useState(false);
    const handleShowModalRewarehousePosTerminal = () => {
        setShowModalRewarehousePosTerminal(true);
    };
    const [showModalScheduledBlacklistPosTerminal, setShowModalScheduledBlacklistPosTerminal] = useState(false);
    const handleShowModalScheduledBlacklistPosTerminal = () => {
        setShowModalScheduledBlacklistPosTerminal(true);
    };
    const [showModalUpdatePosTerminal, setShowModalUpdatePosTerminal] = useState(false);
    const [showModalPosTerminalInfo, setShowModalPosTerminalInfo] = useState(false);
    const [showModalEmergencyRemoteCollection, setShowModalEmergencyRemoteCollection] = useState(false);

    // API Calls
    const { data: dataGeolocation, isLoading: isLoadingGeolocation } =
        actionsPosTerminals.getGeolocation(subscriptionAndTerminal && subscriptionAndTerminal.posTerminal.uuid);
    const { data: dataPlan, refetch: refetchPlan } = actionsPlans.getPlan(
        {
            organizationId: subscriptionAndTerminal && subscriptionAndTerminal.provider,
            planId: subscriptionAndTerminal && subscriptionAndTerminal.subscriptionPlan
        }, false);
    useEffect(() => {
        if (hasPlanDetailsPermission && subscriptionAndTerminal && subscriptionAndTerminal.subscriptionPlan) {
            refetchPlan();
        }
    }, [subscriptionAndTerminal, hasPlanDetailsPermission]);

    useEffect(() => {
        if (dataGeolocation?.lat && dataGeolocation?.lon) {
            setLatitude(parseFloat(dataGeolocation.lat));
            setLongitude(parseFloat(dataGeolocation.lon));
        }
    }, [dataGeolocation]);

    // Set the subscription details grid data
    useEffect(() => {
        if (subscriptionAndTerminal && dataPlan) {
            setSubscriptionDetailsGridData({
                ...subscriptionAndTerminal,
                ...(hasPlanDetailsPermission && { subscriptionPlan: { ...dataPlan } })
            });
        }
    }, [dataPlan, hasPlanDetailsPermission, subscriptionAndTerminal]);

    // Render
    return (
        <>
            <Accordion
                type='multiple'
                defaultValue={[
                    POS_TERMINAL_DETAILS_SECTIONS.SUBSCRIPTION,
                    POS_TERMINAL_DETAILS_SECTIONS.TERMINAL,
                    POS_TERMINAL_DETAILS_SECTIONS.EVENTS,
                    POS_TERMINAL_DETAILS_SECTIONS.PAYLUDD
                ]}
            >
                <AccordionItem className='rounded-md' value={POS_TERMINAL_DETAILS_SECTIONS.SUBSCRIPTION} >
                    <AccordionTrigger
                        onClick={() => setOpenSubscription(!openSubscription)}
                        className='opacity-[.8]'
                        open={openSubscription}
                    >
                        <div className='flex items-center gap-2'>
                            <Icon icon={ICONS.SUBSCRIPTION} />
                            <Entity entity='page.subscription.accordions.subscription' />
                        </div>
                    </AccordionTrigger>
                    <AccordionContent>
                        <div>
                            {
                                subscriptionAndTerminal &&
                                <>
                                    <PageActions
                                        actions={
                                            isCustomPay ?
                                                [
                                                    ...CustomPayGenerateActionsSubscriptionLifecycle(
                                                        subscriptionAndTerminal,
                                                        permissions,
                                                        {
                                                            handleShowModalActivateSubscription,
                                                            handleShowModalTerminateSubscription,
                                                            handleShowModalScheduledTerminateSubscription,
                                                            handleShowModalUpdateSubscriptionWithOrWithoutPlan
                                                        }
                                                    )
                                                ]
                                                : [
                                                    ...GenerateActionsSubscriptionLifecycle(
                                                        subscriptionAndTerminal,
                                                        permissions,
                                                        {
                                                            handleShowModalActivateSubscription,
                                                            handleShowModalTerminateSubscription,
                                                            handleShowModalScheduledTerminateSubscription,
                                                            handleShowModalMigrateSubscription,
                                                            handleShowModalUpdateSubscriptionWithOrWithoutPlan
                                                        }
                                                    )
                                                ]
                                        }
                                        retractable
                                    />
                                    <DetailsGrid
                                        data={subscriptionDetailsGridData}
                                        fields={subscriptionDataToDisplay}
                                    />
                                </>
                            }

                        </div>
                    </AccordionContent>
                </AccordionItem>
                <AccordionItem className='rounded-md' value={POS_TERMINAL_DETAILS_SECTIONS.TERMINAL} >
                    <AccordionTrigger
                        onClick={() => setOpenTerminal(!openTerminal)}
                        className='opacity-[.8] rounded-md'
                        open={openTerminal}
                    >
                        <div className='flex items-center gap-2'>
                            <Icon icon={ICONS.POS_TERMINAL} />
                            <Entity entity='page.subscription.accordions.terminal' />
                        </div>
                    </AccordionTrigger>
                    <AccordionContent
                    >
                        <div>
                            {
                                subscriptionAndTerminal.posTerminal &&
                                <>
                                    <PageActions
                                        actions={
                                            [
                                                ...GenerateActionsPosTerminalLifecycle(
                                                    subscriptionAndTerminal,
                                                    permissions,
                                                    {
                                                        handleShowModalTemporarilyAllow,
                                                        handleShowModalBlacklistPosTerminal,
                                                        handleShowModalUnblacklistPosTerminal,
                                                        handleShowModalRewarehousePosTerminal,
                                                        handleShowModalScheduledBlacklistPosTerminal
                                                    }
                                                ),
                                                {
                                                    icon: ICONS.POS_TERMINAL,
                                                    entity: 'actions.row.update_labels',
                                                    namespace: nsTerminal,
                                                    onClick: () => setShowModalUpdatePosTerminal(true),
                                                    visible: true,
                                                    disabled: !hasRequiredPermissions({
                                                        permissions: permissions,
                                                        requiredPermissions: [Permissions.POS_TERMINAL_UPDATE.name]
                                                    }),
                                                    tooltipForbidden: 'title.permissions.access_denied'
                                                },
                                                !isCustomPay && {
                                                    icon: ICONS.ADD_ENVELOPES,
                                                    entity: 'actions.row.add_envelope',
                                                    namespace: nsSubscription,
                                                    onClick: () => setShowModalAddEnvelope(true),
                                                    visible: true,
                                                    disabled: !hasRequiredPermissions({
                                                        permissions: permissions,
                                                        requiredPermissions: [Permissions.CSV_INSERT_POS_REMOTE_DOWNLOAD.name]
                                                    }),
                                                    tooltipForbidden: 'title.permissions.access_denied'
                                                },
                                                !isCustomPay && {
                                                    icon: ICONS.INFO,
                                                    entity: 'actions.row.posTerminal_info_pos_status',
                                                    namespace: nsTerminal,
                                                    onClick: () => setShowModalPosTerminalInfo(true),
                                                    visible: hasRequiredPermissions({
                                                        permissions: permissions,
                                                        requiredPermissions: [Permissions.VISUALISE_POS_TERMINAL_STATUS_ON_GATEWAY.name]
                                                    }),
                                                    tooltipForbidden: 'title.permissions.access_denied'
                                                },
                                                !isCustomPay && {

                                                    icon: ICONS.EMERGENCY_REMOTE_COLLECTION,
                                                    entity: 'actions.row.emergency_collect',
                                                    namespace: nsPayluddSubscription,
                                                    onClick: () => setShowModalEmergencyRemoteCollection(true),
                                                    visible: true,
                                                    disabled: !hasRequiredPermissions({
                                                        permissions: permissions,
                                                        requiredPermissions: [Permissions.TRANSACTIONS_EMERGENCY_REMOTE.name]
                                                    }),
                                                    tooltipForbidden: 'title.permissions.access_denied'
                                                }
                                            ]
                                        }
                                        retractable
                                    />
                                    <DetailsGrid data={subscriptionAndTerminal} fields={posTerminalDataToDisplay} />
                                </>
                            }
                        </div>
                    </AccordionContent>
                </AccordionItem>

                <AccordionItem className='rounded-md' value={POS_TERMINAL_DETAILS_SECTIONS.EVENTS} >
                    <AccordionTrigger
                        onClick={() => setOpenEvents(!openEvents)}
                        className='opacity-[.8]'
                        open={openEvents}
                    >
                        <div className='flex items-center gap-2'>
                            <Icon icon={ICONS.CONNECTION} />
                            <Entity entity='page.subscription.accordions.connection' />
                        </div>
                    </AccordionTrigger>
                    <AccordionContent >
                        <div className='space-y-4'>
                            {
                                subscriptionAndTerminal &&
                                <DetailsGrid data={subscriptionAndTerminal} fields={eventLogDataToDisplay} />
                            }
                            {isLoadingGeolocation &&
                                <Skeleton className='w-full h-80 animate-pulse' />
                            }
                            {
                                dataGeolocation && Number.isFinite(latitude) && Number.isFinite(longitude) &&
                                <OpenStreetMapComponent
                                    center={{
                                        lat: latitude,
                                        lng: longitude
                                    }}
                                />
                            }
                        </div>
                    </AccordionContent>
                </AccordionItem>
                {(payludd || pace) && <AccordionItem className='rounded-md' value={POS_TERMINAL_DETAILS_SECTIONS.PAYLUDD} >
                    <AccordionTrigger
                        onClick={() => setOpenPayludd(!openPayludd)}
                        className='opacity-[.8]'
                        open={openPayludd}
                    >
                        <div className='flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:flex-wrap'>
                            <div className='flex items-center gap-2'>
                                <Icon icon={ICONS.POS_TERMINAL} />
                                <Entity entity='page.subscription.accordions.payludd_terminal_state' />
                            </div>
                            <div className='flex items-center gap-2'>
                                <div
                                    className='flex items-center justify-center rounded-md p-1 shrink-0'
                                    title={String(payluddHeaderColor || '').toUpperCase()}
                                    style={{
                                        backgroundColor: payluddHeaderColor,
                                        width: '40px',
                                        height: '40px'
                                    }}
                                >
                                    {(payluddHeaderLogoSrc || pace) &&
                                        <img
                                            src={pace ? paceLogoSrc : payluddHeaderLogoSrc}
                                            alt=''
                                            className='max-w-full max-h-full object-contain'
                                        />
                                    }
                                </div>
                                {/* {payluddHeaderLastUpdate &&
                                    <span className='text-labelGray-foreground text-sm'>
                                        <RenderDateWithFromTime value={payluddHeaderLastUpdate} aligned />
                                    </span>} */}
                            </div>
                        </div>
                    </AccordionTrigger>
                    <AccordionContent>
                        <PayluddPosTerminalDetails posTerminal={posTerminal} />
                    </AccordionContent>
                </AccordionItem>
                }
            </Accordion>
            {
                // Subscription action
                showModalActivateSubscription &&
                <ModalActivateSubscription
                    isOpen={showModalActivateSubscription}
                    setIsOpen={setShowModalActivateSubscription}
                    subscriptionId={subscriptionAndTerminal.uuid}
                    allowedClientId={allowedClientId}
                    directProviderId={directProviderId}
                    shouldUnblacklistPosTerminal={isSubscriptionNotActivatedAndTerminalNotAllowed}
                    setLaunchRefetch={refetch}
                />
            }
            {
                // Subscription action
                showModalTerminateSubscription &&
                <ModalTerminateSubscription
                    isOpen={showModalTerminateSubscription}
                    setIsOpen={setShowModalTerminateSubscription}
                    subscriptionId={subscriptionAndTerminal.uuid}
                    setLaunchRefetch={refetch}
                />
            }
            {
                // Subscription action
                showModalScheduledTerminateSubscription &&
                <ModalScheduledTerminateSubscription
                    isOpen={showModalScheduledTerminateSubscription}
                    setIsOpen={setShowModalScheduledTerminateSubscription}
                    subscriptionId={subscriptionAndTerminal.uuid}
                    setLaunchRefetch={refetch}
                />
            }
            {
                // Subscription action
                showModalMigrateSubscription &&
                <ModalMigrateSubscription
                    isOpen={showModalMigrateSubscription}
                    setIsOpen={setShowModalMigrateSubscription}
                    subscriptionId={subscriptionAndTerminal.uuid}
                    subcriptionStartBillingDate={subscriptionAndTerminal[subscriptionFields.START_BILLING_DATE.key]}
                    setLaunchRefetch={refetch}
                />
            }
            {
                // Subscription action
                showModalUpdateSubscriptionWithOrWithoutPlan &&
                <ModalUpdateSubscriptionWithOrWithoutPlan
                    isOpen={showModalUpdateSubscriptionWithOrWithoutPlan}
                    setIsOpen={setShowModalUpdateSubscriptionWithOrWithoutPlan}
                    subscriptionId={subscriptionAndTerminal.uuid}
                    allowedClientId={subscriptionAndTerminal[subscriptionFields.MERCHANT_ORGANIZATION.key]}
                />
            }
            {
                // Subscription action
                showModalAddEnvelope &&
                <ModalAddEnvelope
                    isOpen={showModalAddEnvelope}
                    setIsOpen={setShowModalAddEnvelope}
                    subscriptionId={subscriptionAndTerminal.uuid}
                    setLaunchRefetch={refetch}
                />
            }

            {
                // POS terminal action
                showModalTemporarilyAllow &&
                <ModalAllowTemporarily
                    isOpen={showModalTemporarilyAllow}
                    setIsOpen={setShowModalTemporarilyAllow}
                    subscriptionId={subscriptionAndTerminal.uuid}
                    setLaunchRefetch={refetch}
                />
            }
            {
                // POS terminal action
                showModalBlacklistPosTerminal &&
                <ModalBlacklistPosTerminal
                    isOpen={showModalBlacklistPosTerminal}
                    setIsOpen={setShowModalBlacklistPosTerminal}
                    subscriptionId={subscriptionAndTerminal.uuid}
                    setLaunchRefetch={refetch}
                />
            }
            {
                // POS terminal action
                showModalUnblacklistPosTerminal &&
                <ModalUnblacklistPosTerminal
                    isOpen={showModalUnblacklistPosTerminal}
                    setIsOpen={setShowModalUnblacklistPosTerminal}
                    subscriptionId={subscriptionAndTerminal.uuid}
                    setLaunchRefetch={refetch}
                />
            }
            {
                // POS terminal action
                showModalRewarehousePosTerminal &&
                <ModalRewarehousePosTerminal
                    isOpen={showModalRewarehousePosTerminal}
                    setIsOpen={setShowModalRewarehousePosTerminal}
                    subscriptionId={subscriptionAndTerminal.uuid}
                    setLaunchRefetch={refetch}
                />
            }
            {
                // POS terminal action
                showModalScheduledBlacklistPosTerminal &&
                <ModalScheduledBlacklistPosTerminal
                    isOpen={showModalScheduledBlacklistPosTerminal}
                    setIsOpen={setShowModalScheduledBlacklistPosTerminal}
                    subscriptionId={subscriptionAndTerminal.uuid}
                    setLaunchRefetch={refetch}
                />
            }
            {
                // POS terminal action
                showModalUpdatePosTerminal &&
                <ModalUpdateLabels
                    isOpen={showModalUpdatePosTerminal}
                    setIsOpen={setShowModalUpdatePosTerminal}
                    posTerminal={subscriptionAndTerminal[subscriptionFields.POS_TERMINAL.key]}
                    setLaunchRefetch={refetch}
                />
            }
            {
                // POS terminal action
                showModalPosTerminalInfo &&
                <ModalShowPosTerminalInfo
                    isOpen={showModalPosTerminalInfo}
                    setIsOpen={setShowModalPosTerminalInfo}
                    subscriptionId={subscriptionAndTerminal.uuid}
                />
            }
            {
                // POS terminal action
                showModalEmergencyRemoteCollection &&
                <ModalEmergencyRemoteCollection
                    isOpen={showModalEmergencyRemoteCollection}
                    setIsOpen={setShowModalEmergencyRemoteCollection}
                    posTerminalId={subscriptionAndTerminal[subscriptionFields.POS_TERMINAL.key].uuid}
                />
            }
        </ >
    );
}

PosTerminalDetailsTab.propTypes = {
    subscriptionAndTerminal: PropTypes.object.isRequired,
    refetch: PropTypes.func.isRequired
};

export default PosTerminalDetailsTab;